import React, {
	useState,
	useMemo,
	useCallback,
	useRef,
	useEffect,
} from 'react'
import * as ls from 'local-storage'

import { ResumeViewProps } from '.'
import {
	Container,
	PDFDocument,
	PDFPage,
	LottieLoaderHolder,
	LottieLoader,
	BottomBlur,
	TextContainer,
	UploadButton, DownloadButton, FixedButtonHolder,
} from './ResumeView.styles'
import Popup from 'components/Popup'
import UploadWarning from 'components/Popups/UploadWarning'

const { FilePicker } = require('react-file-picker')

declare let window: any

export default function ResumeView(
	{
		file,
		onUpload,
		onDownload,
		externalLoading,
		name,
		...props
	}: ResumeViewProps,
) {
	const [pagesCount, setPagesCount] = useState(0)
	const [isLoading, setLoading] = useState(!!file)
	const [uploadWarningShown, setUploadWarningShown] = useState(false)
	const [uploadErrorShown, setUploadErrorShown] = useState(false)
	const userAccount: { hygienistId: number, dentist?: any, hygienist?: any } = ls.get('userAccount') || {}
	const role = Number.isInteger(userAccount.hygienistId) ? 'Hygienist' : 'Dentist'
	const pagesNumbers = useMemo(() => {
		const pagesNumbers = []
		for (let i = 1; i <= pagesCount; i -= -1) {
			pagesNumbers.push(i)
		}
		return pagesNumbers
	}, [pagesCount])

	const getErrorType = (error: string) => {
		if(error){
			return error.split(' ')[0] === 'Must' ? 'extension' : 'size';
		}
	}
	return (
		<Container {...props}>
			{file ? (
				<PDFDocument
					file={file}
					loading={null as any}
					onLoadSuccess={({ numPages }: any) => {
						setPagesCount(numPages)
						setLoading(false)
					}}
					error={
						role === 'Dentist'
							? 'Only resumes uploaded in PDF formats can be previewed here. Please download the resume to view it.'
							: `Preview is only available for PDF files. 
					You have a resume uploaded, but it's just in a file format that can't be previewed here. 
					If you want to upload a new resume, click "Update Resume" below.`
					}
					onSourceError={() => setLoading(false)}
					onLoadError={(err: any) => {
						console.log(err)
						return setLoading(false)
					}}
				>
					{pagesNumbers.map((pageNumber) => (
						<PageView
							key={pageNumber}
							pageNumber={pageNumber}
							pagesCount={pagesCount}
						/>
					))}
				</PDFDocument>
			) : (
				<TextContainer>
					Please upload a resume to be able to submit an application to jobs.
					Resumes need to be uploaded in one of the following formats: JPG, PNG,
					PDF, TIFF or Word Documents.
				</TextContainer>
			)}
			<BottomBlur />
			<FixedButtonHolder isResume={pagesNumbers.length > 0}>
				{onDownload && !isLoading &&(
					<DownloadButton onClick={onDownload}>
						Download{' '}
						{(name as string).split(' ').length > 1 ?
						(name as string).split(' ')[0]
						: name}'s
						Resume
					</DownloadButton> //return only first name
				)}
				{onUpload &&
					(window.cordova ? (
						<UploadButton onClick={() => setUploadWarningShown(true)}>
							{!!file ? 'Update resume' : 'Upload Word Document or PDF'}
						</UploadButton>
					) : (
						<FilePicker
							extensions={['pdf', 'docx', 'doc', 'png', 'tiff', 'jpg', 'jpeg']}
							onChange={onUpload}
							onError={(errMsg: any) => {
								console.log(errMsg)
								setUploadErrorShown(errMsg)
							}}
						>
							<UploadButton>
								{!!file ? 'Update resume' : 'Upload Word Document or PDF'}
							</UploadButton>
						</FilePicker>
					))}
			</FixedButtonHolder>
			<LottieLoaderHolder show={isLoading || (externalLoading as boolean)}>
				<LottieLoader />
			</LottieLoaderHolder>
			<Popup opened={uploadErrorShown}>
				<UploadWarning
					onDone={useCallback(() => {
						setUploadErrorShown(false)
					}, [uploadErrorShown])}
				>
					{getErrorType((uploadErrorShown as any) as string) === 'extension' ? (
						<>
							<h2>Change file format</h2>
							<p>
								To upload your resume, please select an image (JPG), Word, or
								PDF document format.
							</p>
						</>
					) : (
						<>
							<h2>Oh, wait….</h2>
							<p>{uploadErrorShown}</p>
						</>
					)}
				</UploadWarning>
			</Popup>
			<Popup opened={uploadWarningShown}>
				<UploadWarning
					onCancel={useCallback(() => {
						setUploadWarningShown(false)
					}, [setUploadWarningShown])}
					onConfirm={() => eval(`TempStars.Hygienist.Router.goBackPage('contact-support')`) }
				>
					<h2>Oh, wait….</h2>
					<p>
						Resumes must be uploaded from a computer, not a mobile. Please log
						into your account from any computer to upload your resume!
					</p>
					<br />
					<br />
					<p>
					If you’re stuck or need help, please
					&nbsp;
					<a onClick={() => eval(`TempStars.Hygienist.Router.goBackPage('contact-support')`) }>contact us</a>
					&nbsp;
					here and send us your resume. We can upload it for you!

					</p>
				</UploadWarning>
			</Popup>
		</Container>
	)
}

interface PageViewProps {
	pageNumber: number,
	pagesCount: number,
}

function PageView(
	{
		pageNumber,
		pagesCount,
	}: PageViewProps,
) {
	const pageContainerRef = useRef(null)
	const [scale, setScale] = useState(1)

	return (
		<div
			ref={pageContainerRef}
		>
			<p className='pageTitle'>
				Page {pageNumber} of {pagesCount}
			</p>
			<PDFPage
				renderAnnotationLayer={false}
				pageNumber={pageNumber}
				scale={scale}
				onLoadSuccess={
					useCallback((page: any) => {
						const pageContainer = pageContainerRef.current as any
						if (pageContainer !== null) {
							setScale(pageContainer.scrollWidth / page.originalWidth - 0.01)
						}
					}, [pageContainerRef, setScale])
				}
			/>
		</div>
	)
}
